import { callServer, type ServerMethod, type ServerResult } from './server';

/** `{ error, code? }` with the server's status — the shape every screen reads on failure. */
export function failure(result: Extract<ServerResult<unknown>, { ok: false }>): Response {
  return Response.json(
    { error: result.error, ...(result.code && { code: result.code }) },
    { status: result.status },
  );
}

/** Success → `{ [key]: data }`; failure → `{ error, code? }`. */
export function toResponse<T>(result: ServerResult<T>, key: string, status = 200): Response {
  if (!result.ok) return failure(result);
  return Response.json({ [key]: result.data }, { status });
}

/** Success → the server's data as the body itself. */
export function toRawResponse<T>(result: ServerResult<T>): Response {
  if (!result.ok) return failure(result);
  return Response.json(result.data);
}

export const badRequest = (error: string) => Response.json({ error }, { status: 400 });

/** A request's JSON body, or `{}` if it has none / is malformed. */
export async function bodyOf<T = Record<string, unknown>>(req: Request): Promise<T> {
  return (await req.json().catch(() => ({}))) as T;
}

/** The server's admin API. */
export const ADMIN_API = '/api/v1/admin';

/** A path segment, safely encoded. */
export const seg = encodeURIComponent;

/** The request's query string, `?…` included (or empty). */
export const queryOf = (req: Request) => new URL(req.url).search;

/** Calls the server; sends a body only when there is one. */
export function call<T = unknown>(method: ServerMethod, path: string, body?: unknown): Promise<ServerResult<T>> {
  return body === undefined ? callServer<T>(method, path) : callServer<T>(method, path, { body });
}
